import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  CreditCard, Tag, CheckCircle, AlertCircle, Loader2,
  ArrowRight, Phone, ChevronRight, Lock, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';
import { formatMoney } from '../../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StudentCheckout = () => {
  const { settings } = usePublicSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPlanId = searchParams.get('plan');

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [recommendedPlanId, setRecommendedPlanId] = useState(null); // from level test
  const [levelTestInfo, setLevelTestInfo] = useState(null); // { level, matched_plan_name }
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null); // { valid, coupon_id, savings, discounted_amount, message }
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch plans and (optionally) the student's level test result in parallel
        const [plansRes, levelRes] = await Promise.allSettled([
          axios.get(`${API_URL}/api/public/plans`),
          axios.get(`${API_URL}/api/student/level-test-result`),
        ]);

        const loadedPlans = plansRes.status === 'fulfilled' ? plansRes.value.data : [];
        setPlans(loadedPlans);

        let recommendedId = null;
        if (levelRes.status === 'fulfilled' && levelRes.value.data?.has_result) {
          const lr = levelRes.value.data;
          recommendedId = lr.matched_plan_id || null;
          if (lr.level) {
            setLevelTestInfo({ level: lr.level, matched_plan_name: lr.matched_plan_name });
          }
          setRecommendedPlanId(recommendedId);
        }

        // Auto-select: URL param first, then recommended, then only-plan fallback
        if (preselectedPlanId) {
          const match = loadedPlans.find(p => p.id === preselectedPlanId);
          if (match) setSelectedPlan(match);
        } else if (loadedPlans.length === 1) {
          setSelectedPlan(loadedPlans[0]);
        } else if (recommendedId) {
          const match = loadedPlans.find(p => p.id === recommendedId);
          if (match) setSelectedPlan(match);
        }
      } catch {
        toast.error('Failed to load plans');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [preselectedPlanId]);

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    if (!selectedPlan) { toast.error('Select a plan first'); return; }
    setValidatingCoupon(true);
    try {
      const res = await axios.post(`${API_URL}/api/public/coupons/validate`, {
        code: couponCode.trim(),
        plan_id: selectedPlan.id,
        original_amount: selectedPlan.price_brl,
      });
      setCouponResult(res.data);
      if (!res.data.valid) toast.error(res.data.message);
    } catch {
      toast.error('Failed to validate coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setCouponResult(null);
  };

  const handleCheckout = async () => {
    if (!selectedPlan) { toast.error('Please select a plan'); return; }
    setRedirecting(true);
    try {
      const res = await axios.post(`${API_URL}/api/student/checkout/create-session`, {
        plan_id: selectedPlan.id,
        coupon_id: couponResult?.valid ? couponResult.coupon_id : null,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setRedirecting(false);
    }
  };

  const finalAmount = couponResult?.valid
    ? couponResult.discounted_amount
    : (selectedPlan?.price_brl ?? 0);

  const savings = couponResult?.valid ? couponResult.savings : 0;

  if (!settings.stripe_enabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAF7] to-[#FDF8F3] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-[#E8EAF0] p-10 max-w-md w-full text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-[#F5A623] mx-auto" />
          <h2 className="text-xl font-bold text-[#1A2E6E]">Online payments unavailable</h2>
          <p className="text-[#1A2E6E]/60 text-sm">
            Online payments aren't enabled yet. Please contact us to arrange payment.
          </p>
          {settings.whatsapp_url && (
            <a
              href={settings.whatsapp_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-xl font-semibold hover:bg-[#20B858] transition-colors w-full justify-center"
            >
              <Phone size={16} /> Contact via WhatsApp
            </a>
          )}
          <Link to="/student/pending" className="block text-sm text-[#1A2E6E]/50 hover:text-[#1A2E6E]">
            ← Back
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAF7] via-[#FDF8F3] to-[#FAFAF7] px-4 py-12">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <Link to="/">
            <img src="/logo.png" alt="Frankly Talking English" className="h-10 w-auto mx-auto mb-6" />
          </Link>
          <h1 className="text-2xl font-bold text-[#1A2E6E] font-['Sora']">Choose your plan</h1>
          <p className="text-[#1A2E6E]/60 mt-1 text-sm">Secure payment via Stripe</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
          </div>
        ) : (
          <>
            {/* Plan selector */}
            <div className="space-y-3">
              {plans.map(plan => {
                const active = selectedPlan?.id === plan.id;
                return (
                  <motion.button
                    key={plan.id}
                    onClick={() => handleSelectPlan(plan)}
                    whileTap={{ scale: 0.99 }}
                    className={`w-full p-5 rounded-2xl border-2 text-left transition-all ${
                      active
                        ? 'border-[#1A2E6E] bg-[#1A2E6E]/3 shadow-sm'
                        : plan.id === recommendedPlanId
                          ? 'border-[#F5A623]/60 bg-[#F5A623]/3 hover:border-[#F5A623]'
                          : 'border-[#E8EAF0] bg-white hover:border-[#1A2E6E]/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                            active ? 'border-[#1A2E6E] bg-[#1A2E6E]' : 'border-[#D1D5DB]'
                          }`}>
                            {active && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-bold text-[#1A2E6E]">{plan.name}</p>
                              {plan.id === recommendedPlanId && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F5A623]/15 text-[#C97D0A] text-[10px] font-semibold">
                                  <Sparkles size={10} />
                                  {levelTestInfo?.level ? `Recommended for ${levelTestInfo.level}` : 'Recommended for your level'}
                                </span>
                              )}
                            </div>
                            {plan.description && (
                              <p className="text-xs text-[#1A2E6E]/50 mt-0.5">{plan.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-4 mt-3 ml-8 text-xs text-[#1A2E6E]/60">
                          <span>{plan.credits} lessons</span>
                          <span>{plan.validity_days} days</span>
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-xl font-bold text-[#1A2E6E]">{formatMoney(plan.price_brl, plan.currency)}</p>
                        <p className="text-xs text-[#1A2E6E]/40 mt-0.5">{plan.currency || 'EUR'}</p>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Coupon */}
            {selectedPlan && (
              <div className="bg-white rounded-2xl border border-[#E8EAF0] p-5">
                <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                  <Tag size={14} className="inline mr-1.5" />Discount coupon <span className="font-normal text-[#1A2E6E]/40">(optional)</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponResult(null); }}
                    onKeyDown={e => e.key === 'Enter' && validateCoupon()}
                    placeholder="e.g. WELCOME20"
                    className="flex-1 px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none font-mono uppercase tracking-wider text-sm"
                  />
                  <button
                    onClick={validateCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                    className="px-4 py-3 rounded-xl bg-[#1A2E6E] text-white text-sm font-medium hover:bg-[#16275C] disabled:opacity-40 transition-colors"
                  >
                    {validatingCoupon ? <Loader2 size={14} className="animate-spin" /> : 'Apply'}
                  </button>
                </div>
                {couponResult && (
                  <div className={`mt-2 px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                    couponResult.valid
                      ? 'bg-[#4CAF50]/8 text-[#4CAF50] border border-[#4CAF50]/20'
                      : 'bg-[#D72B2B]/8 text-[#D72B2B] border border-[#D72B2B]/20'
                  }`}>
                    {couponResult.valid
                      ? <><CheckCircle size={14} /> {couponResult.message}</>
                      : <><AlertCircle size={14} /> {couponResult.message}</>
                    }
                  </div>
                )}
              </div>
            )}

            {/* Price summary */}
            {selectedPlan && (
              <div className="bg-white rounded-2xl border border-[#E8EAF0] p-5 space-y-2">
                <div className="flex justify-between text-sm text-[#1A2E6E]/70">
                  <span>{selectedPlan.name}</span>
                  <span>{formatMoney(selectedPlan.price_brl, selectedPlan.currency)}</span>
                </div>
                {savings > 0 && (
                  <div className="flex justify-between text-sm text-[#4CAF50]">
                    <span>Discount ({couponResult.code})</span>
                    <span>−{formatMoney(savings, selectedPlan.currency)}</span>
                  </div>
                )}
                <div className="pt-2 border-t border-[#E8EAF0] flex justify-between font-bold text-[#1A2E6E]">
                  <span>Total</span>
                  <span className="text-lg">{formatMoney(finalAmount, selectedPlan.currency)}</span>
                </div>
              </div>
            )}

            {/* CTA */}
            <button
              onClick={handleCheckout}
              disabled={!selectedPlan || redirecting}
              className="w-full py-4 rounded-2xl bg-[#D72B2B] text-white font-bold text-base hover:bg-[#B71C1C] transition-colors disabled:opacity-40 flex items-center justify-center gap-3"
            >
              {redirecting
                ? <><Loader2 size={18} className="animate-spin" /> Redirecting to payment…</>
                : <><Lock size={16} /> Pay {selectedPlan ? formatMoney(finalAmount, selectedPlan.currency) : ''} securely <ArrowRight size={16} /></>
              }
            </button>

            <div className="flex items-center justify-center gap-2 text-xs text-[#1A2E6E]/40">
              <Lock size={12} />
              <span>Payments processed securely by Stripe. We never store your card details.</span>
            </div>

            <div className="text-center">
              <Link to="/student/pending" className="text-sm text-[#1A2E6E]/50 hover:text-[#1A2E6E] transition-colors">
                ← Pay later / talk to consultant
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StudentCheckout;
